import * as React from "react";
import NextApp from "next/app";
import getConfig from "next/config";
import Head from "next/head";
import Script from "next/script";
import { setServiceObject, ImBox, TrustedShops, TrustedShopsWidgetScript } from "@grandvisionhq/analytics";
import { AuthProvider, useCommonUserProperties } from "@grandvisionhq/auth";
import { MetaProvider, BloomreachApi } from "@grandvisionhq/bloomreach";
import { MediaQuery, GoogleMapsProvider, ToastContainer } from "@grandvisionhq/common";
import { assertSafePath, RoutingProvider } from "@grandvisionhq/frk-routing";
import { StockIndicatorProvider } from "@grandvisionhq/inventory";
import { ToastProvider } from "@grandvisionhq/iris-ui";
import { AddressConfigurationProvider } from "@grandvisionhq/locations";
import { withConsent, withRedux, withRecaptcha, withApollo } from "@grandvisionhq/next";
import { AlgoliaAnalytics } from "@grandvisionhq/search";
import { useIntl, ReduxProvider, FeatureToggleProvider, IntlProvider, ApiConfigProvider } from "@grandvisionhq/state";
import { FeatureTogglesControlPanel } from "@grandvisionhq/svc-feature-toggles";
import { MonetateScript } from "@grandvisionhq/svc-monetate";
import { TealiumEventPropertiesProvider } from "@grandvisionhq/svc-tealium/events";
import { TealiumProvider } from "@grandvisionhq/svc-tealium/provider";
import { WishlistProvider } from "@grandvisionhq/svc-wishlist";
import { WordLift } from "@grandvisionhq/svc-wordlift";
import { QueryString, compose, LocaleUtils } from "@grandvisionhq/utils";
import { f as featureToggles, a as apiConfig } from "../feature-toggles.js";
import "../google-tag-manager.js";
import "@grandvisionhq/fun-products";
import "../use-checkout-event-manager.js";
import { useCartStorage } from "@grandvisionhq/lib-utils-cart";
import "@grandvisionhq/carts-v2";
import "@grandvisionhq/checkout-v2";
import { useRouter } from "next/router";
import { OneTrustCookieConsent } from "@grandvisionhq/cookie-consent";
import "@grandvisionhq/svc-feature-toggles/dist/index.css";
const useCommonTealiumProperties = ({ opcoCode = "" }) => {
  const { locale, country, currency } = useIntl();
  const [cartData] = useCartStorage();
  const uppercasedLanguage = new Intl.Locale(locale).language.toUpperCase();
  const isPhone = MediaQuery.useIsPhone();
  const isDesktop = MediaQuery.useIsDesktop();
  let deviceType = "tablet";
  if (isPhone) {
    deviceType = "phone";
  } else if (isDesktop) {
    deviceType = "desktop";
  }
  return {
    Page_Country: country,
    Page_Language: uppercasedLanguage,
    Order_Currency: currency,
    Order_CartId: cartData?.cartId ? cartData.cartId : "",
    Page_Brand: opcoCode,
    Page_DeviceType: deviceType,
    Page_Platform: "WEB"
    // TODO: Verify what the actual value here should be
  };
};
const useNextRouter = ({ locale }) => {
  const router = useRouter();
  const onRoutingChangeComplete = useNextRouterEvent("routeChangeComplete");
  const replace = React.useCallback(
    (url) => {
      try {
        assertSafePath(url);
      } catch (e) {
        console.error("Error while calling router.replace: ", e);
        return Promise.resolve(false);
      }
      return router.replace(url);
    },
    [router.replace]
  );
  const push = React.useCallback(
    (url) => {
      try {
        assertSafePath(url);
      } catch (e) {
        console.error("Error while calling router.push: ", e);
        return Promise.resolve(false);
      }
      return router.push(url);
    },
    [router.push]
  );
  return {
    replace,
    push,
    events: { onRoutingChangeComplete },
    locale,
    pathname: router.asPath,
    query: new URLSearchParams(QueryString.stringify(router.query))
  };
};
function useNextRouterEvent(eventName) {
  const router = useRouter();
  const [handler, setHandler] = React.useState();
  const subscribe = React.useCallback((handler2) => {
    setHandler(handler2);
    return () => setHandler(null);
  }, []);
  React.useEffect(() => {
    const nextRouteChangeHandler = (url) => {
      handler?.(url);
    };
    if (handler) {
      router.events.on(eventName, nextRouteChangeHandler);
    }
    return () => {
      router.events.off(eventName, nextRouteChangeHandler);
    };
  }, [eventName, handler, router.events]);
  return subscribe;
}
const { publicRuntimeConfig: publicRuntimeConfig$1 } = getConfig();
function getConsentVendorSettings(defaultLocale, locale) {
  const { cookiepro_url, cookiepro_data_domain, oneTrust } = publicRuntimeConfig$1;
  const oneTrustConfig = {
    locale: defaultLocale,
    isMultilingual: locale.length > 1,
    src: cookiepro_url || oneTrust?.scriptSrc,
    domain: cookiepro_data_domain || oneTrust?.dataDomain,
    skipScriptAfterConsent: oneTrust?.skipScriptAfterConsent ?? false,
    ScriptComponent: Script
  };
  return /* @__PURE__ */ React.createElement(OneTrustCookieConsent, { ...oneTrustConfig });
}
const { publicRuntimeConfig } = getConfig();
const bloomreachSettings = publicRuntimeConfig.bloomreach ?? {};
const createAppComponent = async (options) => {
  const { defaultLocale, consentProvider, settings } = options;
  const { intl } = settings;
  const { isMultilingualEnabled } = featureToggles;
  const withConsent$1 = withConsent(consentProvider);
  if (publicRuntimeConfig.enableApiMocking) {
    const { setupMockServiceWorker } = await import("@grandvisionhq/mock-backend");
    console.log(`> Starting client using mocked backend!`);
    await setupMockServiceWorker({
      api: {
        productsUrl: `${process.env.GRANDVISION_API_URL}/products`,
        bloomreachUrl: process.env.BLOOMREACH_CMS_URL_LIVE,
        authApiUrl: process.env.AUTH0_CUSTOM_DOMAIN
      }
    });
  }
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    bloomreach,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    deliveryIndicator,
    stockIndicator,
    monetate,
    lime,
    trustedShops,
    tealium,
    imBox,
    site,
    selfUrl,
    algolia
  } = publicRuntimeConfig;
  const metaContextValue = {
    site,
    selfUrl,
    headComponent: Head,
    algolia: {
      apiKey: algolia.apiKey,
      appId: algolia.appId,
      indexes: algolia.indexes,
      productIndex: {
        type: algolia.productIndex.type,
        name: algolia.productIndex.name,
        facets: algolia.productIndex.facets,
        targetPath: algolia.productIndex.targetPath
      }
    },
    bloomreach: {
      channel: bloomreach.channel
    }
  };
  const ConditionalRoutingWrapper = settings.routing ? ({ children, locale }) => {
    const router = useNextRouter({ locale });
    return /* @__PURE__ */ React.createElement(
      RoutingProvider,
      {
        formatters: options.settings.routing?.formatters || [],
        router,
        isMultilingual: isMultilingualEnabled
      },
      children
    );
  } : ({ children }) => /* @__PURE__ */ React.createElement(React.Fragment, null, children);
  const ConditionalTealiumWrapper = ({ children }) => {
    const commonTealiumProperties = useCommonTealiumProperties({
      opcoCode: tealium?.opcoCode
    });
    const getCommonUserProperties = useCommonUserProperties();
    if (tealium?.sdkUrl && tealium?.opcoCode) {
      return /* @__PURE__ */ React.createElement(
        TealiumEventPropertiesProvider,
        {
          commonEventProperties: commonTealiumProperties,
          getCommonUserProperties
        },
        /* @__PURE__ */ React.createElement(TealiumProvider, { sdkUrl: tealium.sdkUrl, enableCookieCheck: tealium.enableCookieCheck }, children)
      );
    }
    return /* @__PURE__ */ React.createElement(React.Fragment, null, children);
  };
  const App = ({ Component, pageProps, store, labels, locale, initialBreakpoint }) => {
    React.useEffect(() => {
      setServiceObject();
    }, []);
    const googleMapsSettings = pageProps?.zoomLevel ? {
      ...settings.googleMaps,
      defaultZoom: pageProps?.zoomLevel,
      hasCustomZoomLevel: true
    } : settings.googleMaps;
    return /* @__PURE__ */ React.createElement(React.StrictMode, null, /* @__PURE__ */ React.createElement(Head, null, /* @__PURE__ */ React.createElement("meta", { name: "viewport", content: "width=device-width, initial-scale=1" })), monetate?.script && /* @__PURE__ */ React.createElement(MonetateScript, { src: monetate.script, ScriptComponent: Script }), imBox && imBox.id && /* @__PURE__ */ React.createElement(ImBox, { imBoxId: imBox.id, ScriptComponent: Script }), trustedShops && trustedShops.id && /* @__PURE__ */ React.createElement(TrustedShops, { trustedShopsId: trustedShops.id, ScriptComponent: Script }), trustedShops && trustedShops.widgetId && /* @__PURE__ */ React.createElement(TrustedShopsWidgetScript, { ScriptComponent: Script }), /* @__PURE__ */ React.createElement(
      WordLift,
      {
        ScriptComponent: Script,
        isWordLiftScriptEnabled: featureToggles.isWordLiftScriptEnabled
      }
    ), getConsentVendorSettings(defaultLocale, Object.keys(intl)), /* @__PURE__ */ React.createElement(
      AlgoliaAnalytics,
      {
        apiKey: publicRuntimeConfig.algolia.apiKey,
        appId: publicRuntimeConfig.algolia.appId,
        HeadComponent: Head,
        ScriptComponent: Script
      }
    ), /* @__PURE__ */ React.createElement(ConditionalRoutingWrapper, { locale }, /* @__PURE__ */ React.createElement(ReduxProvider, { store }, /* @__PURE__ */ React.createElement(FeatureToggleProvider, { featureToggles }, /* @__PURE__ */ React.createElement(IntlProvider, { settings: settings.intl[locale], labels }, /* @__PURE__ */ React.createElement(MediaQuery.Provider, { initialBreakpoint }, /* @__PURE__ */ React.createElement(ConditionalTealiumWrapper, null, /* @__PURE__ */ React.createElement(WishlistProvider, null, /* @__PURE__ */ React.createElement(ApiConfigProvider, { apiConfig }, /* @__PURE__ */ React.createElement(GoogleMapsProvider, { settings: googleMapsSettings }, /* @__PURE__ */ React.createElement(StockIndicatorProvider, { settings: stockIndicator }, /* @__PURE__ */ React.createElement(AuthProvider, { settings: settings.auth }, /* @__PURE__ */ React.createElement(AddressConfigurationProvider, { settings: settings.address }, /* @__PURE__ */ React.createElement(MetaProvider, { value: metaContextValue }, /* @__PURE__ */ React.createElement(
      ToastProvider,
      {
        closeButtonDescription: labels["common.actions.close"] || ""
      },
      /* @__PURE__ */ React.createElement(ToastContainer, null),
      /* @__PURE__ */ React.createElement(FeatureTogglesControlPanel, null),
      /* @__PURE__ */ React.createElement(Component, { ...pageProps, labels, store })
    )))))))))))))));
  };
  App.getInitialProps = async (appContext) => {
    const { ctx } = appContext;
    const localeFromCookies = LocaleUtils.getLocaleFromCookie();
    const receivedLocale = ctx.req?.locale?.toString();
    let locale;
    if (receivedLocale && receivedLocale in settings.intl) {
      locale = receivedLocale;
    } else if (!ctx.req && localeFromCookies) {
      locale = localeFromCookies;
    } else {
      locale = options.defaultLocale;
    }
    const initialBreakpoint = ctx.req?.mediaQueryBreakpoint || MediaQuery.BreakpointName.PHONE;
    const tasks = [
      // we need to inject our locale to nextjs context otherwise Pages getInitialProps will not receive it
      NextApp.getInitialProps({
        ...appContext,
        // @ts-expect-error Applying FeatureToggles type to NextApp is tricky
        ctx: { ...appContext.ctx, locale, featureToggles }
      }),
      null
    ];
    const rehydratedLabels = ctx.labels;
    if (!rehydratedLabels) {
      tasks[1] = BloomreachApi.getLabels(
        bloomreachSettings.apiUrl,
        bloomreachSettings.channel,
        ["*"],
        locale
      );
    }
    const [appProps, labels] = await Promise.all(tasks);
    return {
      ...appProps,
      initialBreakpoint,
      locale,
      labels: rehydratedLabels || labels || {}
    };
  };
  const ComposedApp = compose(
    withRedux,
    withRecaptcha(),
    withApollo(),
    withConsent$1()
  )(App);
  return ComposedApp;
};
export {
  createAppComponent
};
//# sourceMappingURL=app.js.map
